// src/utils/phoneCountryUtils.js
/**
 * Shared utilities for the phone-number country selector used across
 * ContactInfoSlide, ContactInfoPage, SaveReportPopup, and ContactForm.
 *
 * IMPORTANT: selectedCountry state in every component is always an ISO 3166-1
 * alpha-2 code (e.g. 'GB'). The calling code (+44) is derived on the fly via
 * getCountryCallingCode(). Nothing stored in Firestore changes.
 */

import { getCountries } from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en.json';

/**
 * Key markets shown at the TOP of the selector, in this exact order.
 * All remaining countries follow alphabetically by display name.
 */
const PRIORITY_COUNTRIES = [
  'GB', // United Kingdom  — primary market
  'US', // United States
  'DE', // Germany
  'FR', // France
  'SE', // Sweden
  'NO', // Norway
  'DK', // Denmark
  'NL', // Netherlands
  'BE', // Belgium
  'IT', // Italy
  'ES', // Spain
  'CH', // Switzerland
  'AT', // Austria
  'IE', // Ireland
  'PL', // Poland
  'AU', // Australia
  'CA', // Canada
  'AE', // United Arab Emirates
  'SA', // Saudi Arabia
];

// Module-level cache so the sort only runs once
let _sortedCache = null;

/**
 * Returns two arrays of ISO alpha-2 codes:
 *   priority – key markets in defined order
 *   rest     – every other country sorted A→Z by display name
 *
 * The result is memoised after the first call.
 */
export function getSortedCountries() {
  if (_sortedCache) return _sortedCache;

  const all = getCountries();
  const prioritySet = new Set(PRIORITY_COUNTRIES);

  const priority = PRIORITY_COUNTRIES.filter((c) => all.includes(c));
  const rest = all
    .filter((c) => !prioritySet.has(c))
    .sort((a, b) => (en[a] || a).localeCompare(en[b] || b));

  _sortedCache = { priority, rest };
  return _sortedCache;
}

/**
 * Returns the human-readable display name for an ISO country code.
 * e.g. getCountryName('GB') → 'United Kingdom'
 */
export function getCountryName(code) {
  return en[code] || code;
}

/**
 * Returns true if the given string is a recognised ISO alpha-2 country code.
 * Used to validate initialCountryCode props before restoring them.
 */
export function isValidCountryCode(code) {
  return !!code && getCountries().includes(code);
}

/**
 * Detects the user's country from the browser's language preferences.
 * Iterates through navigator.languages so that e.g. ['en-GB', 'en'] correctly
 * returns 'GB'.
 *
 * Falls back to 'GB' (primary market) when no region tag can be found.
 *
 * This only affects the pre-selected default — the user can always change it.
 */
export function detectCountryFromLocale(fallback = 'GB') {
  try {
    const all = getCountries();
    const langs =
      (navigator.languages && navigator.languages.length > 0)
        ? Array.from(navigator.languages)
        : [navigator.language || ''];

    for (const lang of langs) {
      // e.g. 'en-GB' → 'GB',  'de-DE' → 'DE'
      const region = lang.split('-')[1]?.toUpperCase();
      if (region && all.includes(region)) {
        return region;
      }
    }
  } catch {
    // SSR guard or permission restriction — fall through to default
  }
  return fallback;
}
